/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	Input,
	ChangeDetectionStrategy,
	Self,
	Optional,
	ChangeDetectorRef,
	OnInit,
	Output,
	EventEmitter,
} from '@angular/core';
import { TicketEditBaseComponent } from '../ticket-edit-base/ticket-edit-base.component';
import { UntypedFormGroup, NgControl } from '@angular/forms';
import { filter, takeUntil, distinctUntilChanged, map } from 'rxjs/operators';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { dateCache, TicketType, ActualPhase, Side } from '@touchplan/common';
import { MultiWrapperComponent } from '../multi-wrapper/multi-wrapper.component';
import { TicketEditService } from '../../ticket-edit.service';
import { EditCombinedData, EditTicketsCombined } from 'src/app/app-data/models/EditTicketsCombined';
import { DatePositionValidator } from '@appLegacy/services/date-position-validator.factory';
import {
	ITicketEditUpdateStatusDialogData,
	IUpdateFields,
	TicketEditDialogComponent,
} from 'src/app/common/docked-ticket/components/ticket-edit-dialog/ticket-edit-dialog.component';
import { PlanState } from 'src/app/services/plan-state.service';
import * as appStrings from '@touchplan/strings';
import { MatDialog } from '@angular/material/dialog';

/**
 * Displays dates and allows selection via a calendar.  Uses the
 * primeng p-calendar component and adds clear and cancel buttons.
 */
@Component({
	selector: 'ticket-calendar',
	templateUrl: './ticket-calendar.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketCalendarComponent extends TicketEditBaseComponent implements OnInit {
	@Input() public editForm: UntypedFormGroup;
	@Input() public open$?: Observable<boolean> = new Observable();
	@Input() public forceErrorWhenFinishNotSelected = true;
	@Input() public formControlToInvalidate = null;
	@Input() public readOnly = false;

	public editDialogFormGroupChange$: Subject<IUpdateFields> = new Subject();
	public liveStart: string;
	public liveFinish: string;
	public ticketType: string;
	public dateDisplay$ = new BehaviorSubject('');
	private _etc: EditTicketsCombined;

	constructor(
		@Self() @Optional() public control: NgControl,
		@Optional() public multiWrapper: MultiWrapperComponent,
		@Optional() protected cd: ChangeDetectorRef,
		public ticketEditService: TicketEditService,
		private planState: PlanState,
		public dialog: MatDialog
	) {
		super(control, multiWrapper, cd);
	}

	public ngOnInit() {
		super.ngOnInit();

		if (this.forceErrorWhenFinishNotSelected && !this.formControlToInvalidate) {
			this.formControlToInvalidate = this.ticketEditService.getEditFormControl('liveDateRange');
		}

		this.editDialogFormGroupChange$
			.pipe(distinctUntilChanged(), takeUntil(this.shutdownSubject))
			.subscribe((data) => {
				this.updateValues(data);
			});

		// Used to open the calendar externally when actualizing state is set for the first time
		this.open$
			.pipe(
				filter((thing) => thing === true),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((openCalendar: boolean) => {
				this.openCalendar();
			});

		this.editForm
			.get('liveDateRange')
			?.valueChanges.pipe(
				map((liveDateRange) => {
					return dateCache.ticketDateStringConvert(liveDateRange[0], liveDateRange[1]);
				}),
				takeUntil(this.shutdownSubject)
			)
			.subscribe(this.dateDisplay$);

		// Used to override the message string
		this.ticketEditService.combinedEdit$.pipe(takeUntil(this.shutdownSubject)).subscribe((etc) => {
			if (etc) {
				this._etc = etc;
				if (etc.data.liveStart && etc.data.liveFinish) {
					this.liveStart = etc.data.liveStart;
					this.liveFinish = etc.data.liveFinish;

					this.dateDisplay$.next(dateCache.ticketDateStringConvert(this.liveStart, this.liveFinish));
				}
			}
		});

		// set date string on initial load
		if (this.liveStart && this.liveFinish) {
			this.dateDisplay$.next(dateCache.ticketDateStringConvert(this.liveStart, this.liveFinish));
		}
	}

	get yearRange(): string {
		return DatePositionValidator.getYearRange(5, 5);
	}

	private updateValues(data) {
		const key = Object.keys(data)[0];
		if (key === 'liveStart' || key === 'liveFinish') {
			// date form values in mobile update status to be updated accordingly to desktop editForm
			return this.updateDateValues(data);
		} else if (key === 'liveStartAdapter' || key === 'liveFinishAdapter') {
			// form controls in mobile update status dialog to be ignored
			return;
		} else {
			this.editForm.get(key)?.setValue(data[key]);
			this.editForm.get(key)?.markAsDirty();
		}
	}

	private updateDateValues(data) {
		const ticketType = this._etc.data.type;
		let liveDateRange = null;
		if ((ticketType === TicketType.Milestone || ticketType === TicketType.Constraint) && data.liveFinish) {
			liveDateRange = [data.liveFinish, data.liveFinish];
			this.liveFinish = data.liveFinish;
		} else {
			if (data.liveStart) {
				liveDateRange = [data.liveStart, this.liveFinish];
				this.liveStart = data.liveStart;
			}
			if (data.liveFinish) {
				liveDateRange = [this.liveStart, data.liveFinish];
				this.liveFinish = data.liveFinish;
			}
			// If liveStart is greater than liveFinish, don't register change in liveDateRange and let dialog form handle error
			// This prevents bad workdays from being generated, for tasks
			if (this.liveStart > this.liveFinish) {
				return;
			}
		}
		this.onChange(liveDateRange);
		this.editForm.get('liveDateRange').markAsDirty();
	}

	public openCalendar() {
		// Don't open when this control is read-only:
		if (this.readOnly) {
			return;
		}
		// Don't open on actualized tickets or plannedPreview
		// open$ can fire openCalendar to open before this._ect is set, we want to make sure the phase checks only happen when ._ect is set
		// If etc is undefined we're _assuming_ it's a side effect of "updateWithSatus".
		// This isn't really a correct assumption, but it should be fine as long as we don't touch it. And hopefully it's replaced soon
		if (
			this._etc &&
			(this._etc.data.actualPhase === ActualPhase.plannedPreview ||
				this._etc.data.actualPhase === ActualPhase.actualized ||
				this._etc.data.side !== Side.ACTIVE)
		) {
			return;
		}
		const ticket$ = this.ticketEditService.combinedEdit$.pipe(
			map((val: EditCombinedData) => Array.from(val.selectedList.values())[0])
		);
		const roles$ = this.planState.rolesApi.data.list$.pipe(map((r) => Array.from(r.values())));
		const locations$ = this.planState.locationsApi.data.list$.pipe(map((l) => Array.from(l.values())));
		const projectMembers$ = this.planState.projectMembersApi.data.list$.pipe(
			map((pm) => Array.from(pm.values()))
		);
		const priorityList$ = this.planState.priorities.list$.pipe(map((p) => Array.from(p.values())));
		const varianceList$ = this.planState.variances.list$.pipe(map((p) => Array.from(p.values())));
		const activeLineDate$ = this.planState.plan.object$.pipe(
			map((plan) => {
				if (!plan) {
					return null;
				}
				return plan.view.activeLineDate || null;
			}),
			distinctUntilChanged(),
			takeUntil(this.shutdownSubject)
		);
		const ticketEditDialogData = {
			title: appStrings.UPDATE_STATUS,
			saveButtonText: appStrings.CLOSE,
			ticket$,
			roleList$: roles$,
			locationList$: locations$,
			projectMembers$,
			priorityList$,
			varianceList$,
			editDialogFormGroupChange$: this.editDialogFormGroupChange$,
			isDisabled: false,
			calendarWorkdays: this.planState.calendarWorkdays,
			activeLineDate$,
		} as ITicketEditUpdateStatusDialogData;

		this.dialog.open(TicketEditDialogComponent, {
			data: ticketEditDialogData,
			maxWidth: '600px',
			disableClose: true,
		});
	}
}
